import React, { useRef, useEffect } from "react"
import { Col, Container, Row } from "reactstrap"
import styled from "styled-components"
import vars from "styles/variables"
import PageTemplate from "templates/PageTemplate"
import useLocoScroll from "hooks/useLocoScroll"
import useImagePreloader from "hooks/useImagePreloader"
import Seo from "components/common/Seo"
import useDataCreativity from "hooks/useDataCreativity"
import WhyChooseUs from "components/core/WhyChooseUs"
import BackToTop from "components/common/BackToTop"
import ImgData1 from "assets/images/data1.png"
import ImgData2 from "assets/images/data2.png"
import ImgData3 from "assets/images/data3.png"
import ImgData4 from "assets/images/data4.png"


const preloadSrcList = [ImgData1, ImgData2, ImgData3, ImgData4]

function DataCreativityPage() {

  const videoRef = useRef(null)

  useEffect(() => {
    const videoElement = videoRef.current
    if (!videoElement) return
  
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoElement.play()
        } else {
          videoElement.pause()
        }
      },
      { threshold: 0.5 }
    )
  
    observer.observe(videoElement)
  
    return () => observer.disconnect()
  }, [])

  const { imagesPreloaded } = useImagePreloader(preloadSrcList)
  // const { videosPreloaded } = useVideoPreloader(videoPreloadList)
  const { dataCreativityRef } = useDataCreativity(imagesPreloaded)
  const { locoScrollTop } = useLocoScroll(imagesPreloaded)
  
  if (!imagesPreloaded) return null

  return (
    <StyledDataCreativityPage ref={dataCreativityRef}>
      <Seo title="Data X Creativity" />
      <BackToTop onClick={locoScrollTop} />
      <PageTemplate title="Data X Creativity" hideTitle>
        <Container>
          <Row className="mb-5" data-scroll-section>
            <Col xs="12">
              <StyledTitle
                data-scroll
                data-scroll-speed="2"
                data-scroll-direction="horizontal"
              >
                <span className="data-creativity-title-1">data x</span>
                <span className="data-creativity-title-2">creativity</span>
              </StyledTitle>
            </Col>
          </Row>

          <Row className="mb-5 align-items-start" data-scroll-section>
            <Col xs="12" lg="12">
              <p
                style={{
                  fontSize: "1rem",
                  fontWeight: "500",
                  lineHeight: "1.6",
                }}
              >
                At I/O Atelier, we believe that AI is a powerful tool to enhance
                creativity, not replace it. By integrating data-driven insights
                into our workflows, we eliminate wasteful and repetitive
                processes, freeing up time and resources to focus on what truly
                matters - exceptional design.
                <br /> <br />
                Our philosophy centres on combining the art of design with the
                science of data, paving the way for a future where creativity
                and technology coexist harmoniously.
              </p>
            </Col>
          </Row>

          <Row className="w-100 d-flex justify-content-end" data-scroll-section>
            <Col xs="12">
              <div>
                <h2 style={{ fontWeight: "bold"}}>
                  Our Vision{" "}
                </h2>
                <p
                  style={{
                    fontSize: "2rem",
                    fontWeight: "500",
                    lineHeight: "1.6",
                  }}
                >
                  Our mission is to redefine design through an AI-augmented
                  approach, empowering both designers and clients. By eliminating
                  inefficiencies and leveraging cutting-edge tools.
                </p>
              </div>
            </Col>
          </Row>

          <Row className="mb-5" data-scroll-section>
            <Col xs="12" lg="7">
              <img
                src={ImgData1}
                alt="Net Zero Graph"
                className="img-fluid w-100"
                style={{
                  borderRadius: "20px",
                }}
              />
            </Col>
            <Col xs="12" lg="5">
              <p style={{ marginTop: 24, marginBottom: 0 }}>
                Focus on delivering high-quality design while maintaining
                competitive fee structures.
                <br />
                <br />
                Allocate more resources towards creativity and innovation.
                <br />
                <br />
                Ensure sustainable, data-driven decision-making that de-risks
                projects for clients.
              </p>
            </Col>
          </Row>

          <Row
            className="mb-5 align-items-start"
            data-scroll-section
            style={{ paddingTop: "40px" }}
          >
            <Col xs="12" lg="5">
              <h2>Unique Approach: Integrated Teams</h2>
              <p>
                What sets us apart is our integrated team of Machine Learning
                Engineers, Data Scientists, and UX/UI Product Engineers working
                alongside our design experts. This unique collaboration enables:
              </p>
              <ul>
                <li>
                  Seamless integration of AI tools into the design process.
                </li>
                <li>Data-backed creative decisions</li>
                <li>Faster project delivery without compromising quality.</li>
              </ul>
            </Col>
            <Col xs="12" lg="7">
              <img
                src={ImgData2}
                alt="Net Zero Graph"
                className="img-fluid w-100"
                style={{
                  borderRadius: "20px",
                }}
              />
              <p
                style={{
                  fontSize: "0.7rem",
                  fontWeight: "300",
                  marginTop: "10px",
                  textAlign: "right",
                  paddingRight: "5%",
                }}
              >
                Office Space in Dubai
              </p>
            </Col>
          </Row>

          <Row className="w-100 d-flex justify-content-end" data-scroll-section>
            <div
              className="mb-5"
              style={{
                width: "80%",
                textAlign: "right",
                textAlignLast: "right",
                direction: "ltr",
                paddingRight: "0%",
              }}
            >
              <p
                style={{
                  fontSize: "2rem",
                  fontWeight: "500",
                  lineHeight: "1.6",
                }}
              >
                We believe that the future of design lies in the perfect
                integration of Data x Creativity.
              </p>
            </div>
          </Row>

          <Row className="mb-5 align-items-center" data-scroll-section>
            <Col xs="12" lg="12">
              <GridContainer>
                <LeftImage>
                  <Image src={ImgData3} alt="Top aligned" />
                </LeftImage>

                <TextBlock>
                  <h3>Accelerating Visualization</h3>
                  <p>
                    We've developed custom workflows and in-house apps that
                    allow us to quickly transition from sketches or white card
                    Revit models to rendered CGI visuals in real time. Using
                    tools such as Flux and MidJourney, we provide real-time
                    style options (modern, minimalist, boho chic, etc.) to
                    clients.
                  </p>

                  <h3>Adaptive CGI Editing</h3>
                  <p>
                    Our tools enable rapid modifications of externally rendered
                    CGI images to accommodate client feedback efficiently.
                  </p>

                  <h3>Immersive Experiences</h3>
                  <p>
                    Using AI platforms such as Runway, we expand single-shot CGI
                    visuals into complete animations and walkthroughs,
                    envisioning unrendered spaces and enhancing client
                    understanding.
                  </p>
                </TextBlock>

                <TextBlock>
                  <h3>Data-Driven Decision Making</h3>
                  <p>
                    We leverage raw building data (Wi-Fi connectivity, access
                    control data, meeting room usage, etc.) to create actionable
                    dashboards. This empowers clients with factual insights for
                    future planning and de-risks the decision-making process.
                  </p>

                  <h3>Efficient Collaboration</h3>
                  <p>
                    We integrate tools such as Read.ai for managing meetings,
                    tracking project actions, and ensuring efficient
                    collaboration. With ChatGPT Enterprise, we streamline
                    document preparation, automating standardized formats and
                    enhancing productivity.
                  </p>
                </TextBlock>

                <RightImage>
                  <Image src={ImgData4} alt="Bottom aligned" />
                  <Caption align="right">BA Lounge in Miami Airport</Caption>
                </RightImage>
              </GridContainer>
            </Col>
          </Row>

          <Row className="mb-5 align-items-center" data-scroll-section>
            <Col
              xs="12"
              lg="6"
              className="d-flex flex-column align-content-right"
            >
              <h2 style={{ fontWeight: "bold", textAlign: "left" }}>
                Future Vision: Innovating Real Estate
              </h2>
              <p style={{ textAlign: "left" }}>
                We're not just transforming design; we're innovating the real
                estate sector. In the second quarter of 2025, we will be
                launching our first spin-out app tailored to the industry. This
                marks the beginning of a new chapter where our expertise will
                extend beyond design to deliver cutting-edge digital solutions.
                <br />
                <br />
                Stay tuned for more details as we redefine the intersection of
                technology, data, and real estate.
              </p>
            </Col>

            <Col
              xs="12"
              lg="6"
              className="d-flex flex-column align-items-center"
            >
              <PhoneFrame>
                <PhoneVideo
                  ref={videoRef}
                  muted
                  loop
                  playsInline
                  autoPlay
                  onMouseEnter={e => e.currentTarget.play()}
                >
                  <source
                    src="https://io-atelier-web-legacy.s3.eu-west-1.amazonaws.com/nhabit-splashscreen.mp4"
                    type="video/mp4"
                  />
                </PhoneVideo>
              </PhoneFrame>
            </Col>
          </Row>
        </Container>
        <WhyChooseUs />
      </PageTemplate>
    </StyledDataCreativityPage>
  )
}

export default DataCreativityPage

const StyledDataCreativityPage = styled.div`
  h3 {
    margin: 0 0 16px 0;
    font-size: 24px;
    font-weight: 600;

    @media (max-width: ${vars.screen_sm_max}) {
      font-size: 18px;
    }
  }

  h4 {
    margin: 0 0 16px 0;
    font-size: 24px;
    font-weight: 500;

    @media (max-width: ${vars.screen_sm_max}) {
      font-size: 18px;
    }
  }

  ul {
    padding-left: 16px;

    li {
      margin-bottom: 8px;
    }
  }

  .fade-in-slide {
    opacity: 0;
    transform: translate(50px, 0px);
  }

  .data-creativity-banner {
    opacity: 0;
  }

  .img-human-truth {
    padding-bottom: 52px;
  }
`

const StyledTitle = styled.h2`
  font-size: 56px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: 5px;
  margin-bottom: 0;

  span {
    display: block;
  }

  .data-creativity-title-1 {
    margin-bottom: 5px;
    opacity: 0;
    transform: translate(-50px, 0px);
  }

  .data-creativity-title-2 {
    margin-left: 100px;
    opacity: 0;
    transform: translate(-50px, 0px);

    @media (max-width: ${vars.screen_xs_max}) {
      margin-left: 50px;
    }
  }

  @media (max-width: ${vars.screen_xs_max}) {
    font-size: 38px;
    margin-bottom: 0;
  }
`

const PhoneFrame = styled.div`
  width: 100%;
  max-width: 70%;
  aspect-ratio: 315 / 654.5;
  border: 0.75em solid black;
  border-radius: 4.5em;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  background-color: #000;
  overflow: hidden;

  @media (max-width: ${vars.screen_xs_max}) {
    border-radius: 3em;
  }
`

const PhoneVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4em;

  @media (max-width: ${vars.screen_xs_max}) {
    border-radius: 3em;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1.5fr;
  grid-template-rows: auto auto auto auto auto;
  padding: 20px;

  @media (max-width: ${vars.screen_md_max}) {
    display: flex;
    flex-direction: column;
    padding: 0;
  }
`

const LeftImage = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 6;
  display: flex;
  flex-direction: column;

  @media (max-width: ${vars.screen_md_max}) {
    grid-column: auto;
    grid-row: auto;
  }
`

const RightImage = styled.div`
  grid-column: 3 / 4;
  grid-row: 4 / 6;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;

  @media (max-width: ${vars.screen_md_max}) {
    align-items: flex-start;
    text-align: left;
    grid-column: auto;
    grid-row: auto;
  }
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: left;
  padding: 20px;

  @media (max-width: ${vars.screen_md_max}) {
    grid-column: auto;
    grid-row: auto;
    padding-left: 0;
    padding-right: 0;
  }

  &:nth-child(2) {
    grid-column: 2 / 4;
    grid-row: 1 / 4;
    padding-bottom: 0;

    @media (max-width: ${vars.screen_md_max}) {
      grid-column: auto;
      grid-row: auto;
    }
  }

  &:nth-child(3) {
    padding-top: 0;

    @media (max-width: ${vars.screen_md_max}) {
      grid-column: auto;
      grid-row: auto;
    }
  }
`

const Caption = styled.p`
  font-size: 0.7rem;
  font-weight: 300;
  margin-top: 10px;
  text-align: ${({ align }) => align || "left"};
  padding-right: ${({ align }) => (align === "left" ? "5%" : "0")};
  width: 100%;
`

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
`
